import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Alert,
  Button,
} from '@mui/material';

import { ExpandMore, ExpandLess, ContentCopy } from '@mui/icons-material';
import { useApi } from '@/providers/ApiContext';
import LoadingMask from '@/components/shared/LoadingMask';
import { getManifestAsset } from '@/actions/manifest';
import { getInspectionAsset } from '@/actions/inspectionAsset';
import { getDocumentAsset } from '@/actions/documentAsset';
import type { EntityId, TraceAssetRow } from '@/types';

type EntityType = 'manifest' | 'inspection' | 'document';

interface AccQsureAdminTraceAssetProps {
  entityType: EntityType;
  entityId: EntityId;
  assetKey: string;
}

function AccQsureAdminTraceAsset({
  entityType,
  entityId,
  assetKey,
}: AccQsureAdminTraceAssetProps): React.JSX.Element {
  const api = useApi();
  const [traceData, setTraceData] = useState<TraceAssetRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchTraceData = async (): Promise<void> => {
      if (!entityType || !entityId || !assetKey) return;

      setLoading(true);
      setError('');

      if (!api) return;
      try {
        let response: unknown;

        switch (entityType) {
          case 'manifest':
            response = await getManifestAsset(api, entityId, assetKey);
            break;
          case 'inspection':
            response = await getInspectionAsset(api, entityId, assetKey);
            break;
          case 'document':
            response = await getDocumentAsset(api, entityId, assetKey);
            break;
          default:
            throw new Error('Invalid entity type');
        }

        // Parse the JSON content if it's a string
        let parsedData: TraceAssetRow[];
        if (typeof response === 'string') {
          parsedData = JSON.parse(response) as TraceAssetRow[];
        } else if (response && typeof response === 'object' && 'content' in response) {
          parsedData = JSON.parse(response.content as string) as TraceAssetRow[];
        } else {
          parsedData = response as TraceAssetRow[];
        }

        if (Array.isArray(parsedData)) {
          setTraceData(parsedData);
        } else {
          throw new Error('Trace data is not an array');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch trace data';
        setError(errorMessage);
        console.error('Error fetching trace data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTraceData();
  }, [entityType, entityId, assetKey, api]);

  const toggleRowExpansion = (rowId: number): void => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const renderJsonField = (data: TraceAssetRow | Record<string, unknown>): React.JSX.Element => {
    if (!data) return <span>N/A</span>;
    return (
      <Paper
        variant='outlined'
        sx={{
          p: 1,
          backgroundColor: '#f5f5f5',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          maxHeight: '200px',
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box', // Ensure padding doesn't cause overflow
        }}
      >
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap', // Preserve formatting but allow wrapping
            wordBreak: 'break-all', // Break long words
            overflowWrap: 'anywhere', // Break at any point if needed
            maxWidth: '100%', // Constrain to parent width
            width: '100%',
            boxSizing: 'border-box',
            lineHeight: '1.5', // Improve readability
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    );
  };

  if (loading) {
    return <LoadingMask />;
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3, width: '100%', overflow: 'hidden' }}>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Trace Asset: {assetKey}
      </Typography>

      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        {traceData.map((row, index) => (
          <Box key={index} sx={{ mb: 1, width: '100%', overflow: 'hidden' }}>
            <Paper
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
                bgcolor: expandedRows.has(index) ? 'grey.100' : 'white',
                width: '100%',
                overflow: 'hidden',
              }}
              onClick={() => toggleRowExpansion(index)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ minWidth: '180px', flexShrink: 0 }}>
                  <Typography variant='body2' color='textSecondary'>
                    {row.asctime || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '120px', flexShrink: 0 }}>
                  <Typography variant='body2' fontWeight='medium'>
                    {row.name || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '80px', flexShrink: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color:
                        row.levelname === 'ERROR'
                          ? 'error.main'
                          : row.levelname === 'WARNING'
                            ? 'warning.main'
                            : 'text.secondary',
                      fontWeight: 'medium',
                    }}
                  >
                    {row.levelname || '-'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant='body2' noWrap>
                    {row.message
                      ? String(row.message).length > 50
                        ? `${String(row.message).substring(0, 50)}...`
                        : String(row.message)
                      : '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{ minWidth: '40px', flexShrink: 0, textAlign: 'center' }}
                >
                  <IconButton size='small'>
                    {expandedRows.has(index) ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>
            </Paper>

            <Collapse in={expandedRows.has(index)}>
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <Paper
                  sx={{
                    p: 2,
                    ml: 2,
                    bgcolor: 'grey.50',
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                      Full Row Data
                    </Typography>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          JSON.stringify(row, null, 2),
                        );
                      }}
                      sx={{ minWidth: 'auto' }}
                    >
                      Copy
                    </Button>
                  </Box>
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    {renderJsonField(row)}
                  </Box>
                </Paper>
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default AccQsureAdminTraceAsset;
